import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Database, Trash2 } from "lucide-react";
import { api, errorMessage, unwrap } from "../../api/client";
import { Alert, Button, Card, PageHead, Spinner, useToast } from "../../components/ui";
import { FormattedMessage } from "react-intl";
import { useT } from "../../i18n/I18nProvider";
import { useFmt } from "./format";

export function AdminCache() {
  const qc = useQueryClient();
  const toast = useToast();
  const t = useT();
  const { num } = useFmt();
  const cache = useQuery({ queryKey: ["provider-cache"], queryFn: () => unwrap(api.GET("/v1/admin/provider-cache")) });
  const clear = useMutation({
    mutationFn: () => unwrap(api.POST("/v1/admin/provider-cache/clear", { body: {} })),
    onSuccess: (r) => {
      toast(r.message);
      qc.invalidateQueries({ queryKey: ["provider-cache"] });
    },
    onError: (e) => toast(errorMessage(e), true),
  });
  if (cache.isLoading) return <Spinner />;
  if (cache.error || !cache.data) return <Alert tone="danger">{errorMessage(cache.error)}</Alert>;
  const c = cache.data;
  return (
    <>
      <PageHead title={t("admin.cache.title")} description={t("admin.cache.description")} />
      <div className="grid-3" style={{ marginBottom: 24 }}>
        <div className="card stat">
          <div className="label">{t("admin.cache.status")}</div>
          <div className="value" style={{ fontSize: 20 }}>{c.enabled ? t("admin.cache.enabled") : t("admin.cache.disabled")}</div>
        </div>
        <div className="card stat">
          <div className="label">{t("admin.cache.entries")}</div>
          <div className="value">{num(c.entries)}</div>
        </div>
        <div className="card stat">
          <div className="label">{t("admin.cache.size")}</div>
          <div className="value">
            <FormattedMessage id="admin.cache.sizeMb" values={{ size: num(c.size_bytes / 1024 / 1024, 1), small: (chunks) => <small>{chunks}</small> }} />
          </div>
        </div>
      </div>
      <Card
        title={<span className="row" style={{ gap: 8 }}><Database size={16} /> {c.directory ? <span dir="auto">{c.directory}</span> : t("admin.cache.noDirectory")}</span>}
        description={t("admin.cache.clearHint")}
        actions={
          <Button
            variant="danger"
            icon={<Trash2 />}
            disabled={!c.entries}
            loading={clear.isPending}
            onClick={() => window.confirm(t("admin.cache.clearConfirm")) && clear.mutate()}
          >
            {t("admin.cache.clear")}
          </Button>
        }
      >
        <p className="muted small">{t("admin.cache.replicaNote")}</p>
      </Card>
    </>
  );
}
